import type { MessageModel } from 'types/chat/messages/message';
import { normalizeChatMessageFlow } from 'types/chat/messages/common';
import { extractReplyPayloadWithWeb3Fallback } from 'utils/extract-reply-from-api';
import {
  getMessageBodyFromPayload,
  getMessageMediaTypeFromPayload,
  getMessageNumericIdFromPayload,
  getWhatsappMessageIdFromPayload,
} from 'utils/normalize-chat-message-payload';
import { HEBREW_SNACKBAR, resolveMessageWhatsappErrorCode, resolveStatusHebFromServerStatus } from 'utils/whatsappErrors';

/** Legacy ReceiveMessage ({ messages, phone }) vs message:new ({ toUser, ... }). */
export function unwrapSocketMessageEnvelope(raw: unknown): {
  messageData: Record<string, unknown>;
  toUser: string | null;
} | null {
  if (!raw || typeof raw !== 'object') return null;
  const obj = raw as Record<string, unknown>;

  if (Array.isArray(obj.messages) && obj.messages.length > 0) {
    const first = obj.messages[0];
    if (first && typeof first === 'object') {
      const phone =
        typeof obj.phone === 'string'
          ? obj.phone
          : typeof obj.Phone === 'string'
            ? obj.Phone
            : null;
      return { messageData: first as Record<string, unknown>, toUser: phone };
    }
  }

  const toUser =
    typeof obj.toUser === 'string'
      ? obj.toUser
      : typeof obj.ToUser === 'string'
        ? obj.ToUser
        : typeof obj.phone === 'string'
          ? obj.phone
          : null;

  if (toUser || 'direction' in obj || 'Direction' in obj || 'id' in obj || 'Id' in obj) {
    return { messageData: obj, toUser };
  }

  if ('arguments' in obj && Array.isArray(obj.arguments) && obj.arguments.length > 0) {
    const inner = obj.arguments[0];
    if (inner && typeof inner === 'object') {
      return unwrapSocketMessageEnvelope(inner);
    }
  }

  return null;
}

export function mapSocketPayloadToMessageModel(
  md: Record<string, unknown>,
  toUser: string,
  companyId: string,
): MessageModel {
  const dirRaw = md.direction ?? md.Direction ?? md.Type ?? md.type;
  const dirStr = typeof dirRaw === 'string' ? dirRaw : '';
  const normalizedType = dirStr ? normalizeChatMessageFlow(dirStr) : 'outcoming';

  const waResolved = getWhatsappMessageIdFromPayload(md);
  // קודם התאריך הגולמי: במעטפת הישנה (ReceiveMessage, מנוע הבוטים) DateCreate הוא
  // מחרוזת תצוגה "dd/MM HH:mm" בלי שנה — הדפדפן מפרש אותה כשנת 2001, והשורה
  // צנחה לתחתית הרשימה הממוינת ("נעלמה"). DateCreateRaw נושא את התאריך האמיתי.
  const dateRaw = md.date ?? md.Date ?? md.DateCreateRaw ?? md.DateCreate;
  const dateCandidate = typeof dateRaw === 'string' ? dateRaw : String(dateRaw ?? '');
  // רשת ביטחון: תאריך שלא ניתן לפירוק היה שובר את מיון הרשימה — נופלים לזמן הנוכחי
  const dateStr =
    dateCandidate && !Number.isNaN(new Date(dateCandidate).getTime())
      ? dateCandidate
      : new Date().toISOString();
  const statusRaw = md.status ?? md.Status;
  const statusStr = typeof statusRaw === 'string' ? statusRaw : String(statusRaw ?? '');
  const fileUrlRaw = md.fileUrl ?? md.File ?? md.file;
  const tempRaw = md.tempId ?? md.TempId;
  const objectRaw = md.Object ?? md.object;

  let objectValue: string | null = null;
  if (typeof objectRaw === 'string') {
    objectValue = objectRaw;
  } else if (objectRaw != null && typeof objectRaw === 'object') {
    try {
      objectValue = JSON.stringify(objectRaw);
    } catch {
      objectValue = null;
    }
  }

  const statusHebFromPayloadRaw =
    (typeof md.statusHeb === 'string' ? md.statusHeb : null) ??
    (typeof md.StatusHeb === 'string' ? md.StatusHeb : null);
  const statusHebFromPayload =
    statusHebFromPayloadRaw && statusHebFromPayloadRaw.trim() !== HEBREW_SNACKBAR.genericError
      ? statusHebFromPayloadRaw
      : null;

  const statusHebResolved = statusHebFromPayload ?? resolveStatusHebFromServerStatus(statusStr);

  const whatsappErrorCodeResolved = resolveMessageWhatsappErrorCode({
    Status: statusStr,
    StatusHeb: statusHebFromPayload ?? statusHebResolved,
    whatsappErrorCode:
      (md.whatsappErrorCode as string | number | null | undefined) ??
      (md.WhatsappErrorCode as string | number | null | undefined) ??
      null,
  });

  return {
    Id: getMessageNumericIdFromPayload(md),
    MessageId: waResolved ?? null,
    messageId: waResolved ?? null,
    Message: getMessageBodyFromPayload(md),
    Type: normalizedType,
    DateCreate: dateStr,
    DateCreatedOriginal: dateStr,
    DateCreateRaw: dateStr,
    FileType: getMessageMediaTypeFromPayload(md) as MessageModel['FileType'],
    File: typeof fileUrlRaw === 'string' ? fileUrlRaw : null,
    Status: statusStr as MessageModel['Status'],
    reactions: (md.reaction ?? md.Reaction ?? null) as MessageModel['reactions'],
    Reply: extractReplyPayloadWithWeb3Fallback(md),
    Imported: (md.imported ?? md.Imported ?? null) as MessageModel['Imported'],
    Recovered: (md.recovered ?? md.Recovered ?? null) as MessageModel['Recovered'],
    IsForwarded: (md.isForwarded ?? md.IsForwarded ?? null) as MessageModel['IsForwarded'],
    ToUser: toUser,
    CompanyId: companyId,
    GlobalUserId: (md.GlobalUserId ?? md.globalUserId ?? null) as string | null,
    StatusHeb: statusHebResolved,
    ...(whatsappErrorCodeResolved != null ? { whatsappErrorCode: whatsappErrorCodeResolved } : {}),
    TempId: (typeof tempRaw === 'string' ? tempRaw : null) as string | null,
    tempId: (typeof tempRaw === 'string' ? tempRaw : null) as string | null,
    //* ייחוס לבוט — message:new של שליחת מערכת נושא botName; בלעדיו ההודעה תיוחס לצופה.
    BotName: ((md.botName ?? md.BotName) || null) as string | null,
    //* מקור השליחה — לצ'יפ "תפוצה" ולמניעת ייחוס הודעת תפוצה ללא כותב לצופה.
    SendSource: ((md.sendSource ?? md.SendSource) || null) as string | null,
    Object: objectValue,
  };
}
